/**
 * 사용자 정보 처리 서비스
 * User information processing service
 */

import { decrypt2Async } from '../utils/crypto-utils';

// 사용자 정보 타입 정의
export interface UserInfo {
  id: string;
  name: string;
  department?: string;
  position?: string;
  email?: string;
  phone?: string;
  employeeId?: string;
  rawData?: string; // 원본 암호화된 데이터
}

/**
 * 사용자 정보 파싱 클래스
 * User information parsing class
 */
export class UserService {
  private static instance: UserService;
  private userInfo: UserInfo | null = null;

  private constructor() {}

  /**
   * 싱글톤 인스턴스 반환
   * Return singleton instance
   */
  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  /**
   * URL에서 사용자 정보 추출 및 파싱
   * Extract and parse user information from URL
   * 
   * @param paramName URL 파라미터 이름 / URL parameter name
   * @returns 사용자 정보 객체 / User information object
   */
  public async getUserInfo(paramName: string = 'user'): Promise<UserInfo | null> {
    try {
      // URL에서 암호화된 사용자 정보 추출
      // Extract encrypted user information from URL
      const urlParams = new URLSearchParams(window.location.search);
      const encryptedUser = urlParams.get(paramName);
      
      if (!encryptedUser) {
        console.warn('URL에서 사용자 정보를 찾을 수 없습니다 / User information not found in URL');
        return null;
      }

      // 복호화
      // Decrypt
      let decryptedData: string;
      try {
        decryptedData = await decrypt2Async(encryptedUser);
      } catch (decryptError) {
        console.error('사용자 정보 복호화에 실패했습니다 / User information decryption failed:', decryptError);
        return null;
      }
      
      if (!decryptedData || decryptedData.trim() === '') {
        console.error('복호화된 사용자 정보가 비어있습니다 / Decrypted user information is empty');
        return null;
      }

      // 사용자 정보 파싱
      // Parse user information
      const userInfo = this.parseUserInfo(decryptedData, encryptedUser);
      
      // 사용자 정보 유효성 검사
      // Validate user information
      if (!this.validateUserInfo(userInfo)) {
        console.error('사용자 정보 유효성 검사 실패 / User information validation failed:', userInfo);
        return null;
      }
      
      // 인스턴스에 저장
      // Store in instance
      this.userInfo = userInfo;
      
      console.log('사용자 정보 로드 완료 / User information loaded successfully:', userInfo);
      return userInfo;
      
    } catch (error) {
      console.error('사용자 정보 처리 오류 / Error processing user information:', error);
      return null;
    }
  }

  /**
   * 복호화된 문자열을 사용자 정보 객체로 파싱
   * Parse decrypted string into user information object
   * 
   * @param decryptedData 복호화된 데이터 / Decrypted data
   * @param rawData 원본 암호화된 데이터 / Original encrypted data
   * @returns 파싱된 사용자 정보 / Parsed user information
   */
  private parseUserInfo(decryptedData: string, rawData: string): UserInfo {
    // 기본 사용자 정보 (한동주_123456 형태)
    const baseUserInfo: UserInfo = {
      id: '',
      name: '',
      rawData: rawData
    };

    try {
      // 다양한 형태의 사용자 정보 파싱 시도
      
      // 1. "이름_사번" 형태 (한동주_123456)
      if (decryptedData.includes('_')) {
        const parts = decryptedData.split('_');
        if (parts.length >= 2) {
          baseUserInfo.name = parts[0];
          baseUserInfo.employeeId = parts[1];
          baseUserInfo.id = parts[1]; // 사번을 ID로 사용
        }
      }
      
      // 2. JSON 형태인 경우
      else if (decryptedData.startsWith('{') && decryptedData.endsWith('}')) {
        const jsonData = JSON.parse(decryptedData);
        return {
          ...baseUserInfo,
          ...jsonData,
          rawData: rawData
        };
      }
      
      // 3. 쉼표로 구분된 형태 (이름,사번,부서,직급)
      else if (decryptedData.includes(',')) {
        const parts = decryptedData.split(',');
        baseUserInfo.name = parts[0] || '';
        baseUserInfo.employeeId = parts[1] || '';
        baseUserInfo.department = parts[2] || '';
        baseUserInfo.position = parts[3] || '';
        baseUserInfo.id = parts[1] || '';
      }
      
      // 4. 단순 문자열인 경우
      else {
        baseUserInfo.name = decryptedData;
        baseUserInfo.id = decryptedData;
      }
      
    } catch (error) {
      console.warn('사용자 정보 파싱 중 오류 발생 / Error during user info parsing:', error);
      // 파싱 실패 시 원본 데이터를 이름으로 사용
      baseUserInfo.name = decryptedData;
      baseUserInfo.id = decryptedData;
    }

    return baseUserInfo;
  }

  /**
   * 현재 저장된 사용자 정보 반환
   * Return currently stored user information
   */
  public getCurrentUser(): UserInfo | null {
    return this.userInfo;
  }

  /**
   * 사용자 정보 초기화
   * Initialize user information
   */
  public clearUserInfo(): void {
    this.userInfo = null;
  }

  /**
   * 사용자 정보 유효성 검사
   * Validate user information
   */
  public validateUserInfo(userInfo: UserInfo): boolean {
    return !!(userInfo && userInfo.name && userInfo.id);
  }

  /**
   * 사용자 정보를 로컬 스토리지에 저장
   * Save user information to local storage
   */
  public saveUserToStorage(userInfo: UserInfo): void {
    try {
      localStorage.setItem('kpc_user_info', JSON.stringify(userInfo));
      console.log('사용자 정보가 로컬 스토리지에 저장되었습니다 / User info saved to local storage');
    } catch (error) {
      console.error('로컬 스토리지 저장 오류 / Local storage save error:', error);
    }
  }

  /**
   * 로컬 스토리지에서 사용자 정보 로드
   * Load user information from local storage
   */
  public loadUserFromStorage(): UserInfo | null {
    try {
      const stored = localStorage.getItem('kpc_user_info');
      if (stored) {
        const userInfo = JSON.parse(stored);
        this.userInfo = userInfo;
        return userInfo;
      }
    } catch (error) {
      console.error('로컬 스토리지 로드 오류 / Local storage load error:', error);
    }
    return null;
  }

  /**
   * 로컬 스토리지에서 사용자 정보 삭제
   * Remove user information from local storage
   */
  public removeUserFromStorage(): void {
    try {
      localStorage.removeItem('kpc_user_info');
      console.log('로컬 스토리지에서 사용자 정보가 삭제되었습니다 / User info removed from local storage');
    } catch (error) {
      console.error('로컬 스토리지 삭제 오류 / Local storage remove error:', error);
    }
  }
}

// 싱글톤 인스턴스 내보내기
export const userService = UserService.getInstance();
